import XLSX from "xlsx-js-style";

export interface CellData {
  value: string;
  bold?: boolean;
  italic?: boolean;
  className?: string;
}

export interface SheetData {
  name: string;
  data: CellData[][];
}

/**
 * Parses markdown content into named sheets.
 * Each `## SheetName` heading followed by a markdown table becomes a separate sheet.
 * Tables without a preceding heading get auto-named "Sheet1", "Sheet2", etc.
 */
export function parseMarkdownToSheets(markdown: string): SheetData[] {
  const lines = markdown.trim().split("\n");
  const sheets: SheetData[] = [];

  let currentName: string | null = null;
  let autoIndex = 1;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    // Detect sheet name heading (## SheetName)
    const headingMatch = line.match(/^##\s+(.+)$/);
    if (headingMatch) {
      currentName = headingMatch[1].trim();
      i++;
      continue;
    }

    // Detect a table row (starts and ends with |)
    if (line.startsWith("|") && line.endsWith("|")) {
      const headerCells = parsePipeLine(line);

      // Next line should be the separator (|---|---|)
      if (i + 1 < lines.length && /^\|[\s\-:|]+\|$/.test(lines[i + 1].trim())) {
        i += 2; // skip header + separator
        const rows: CellData[][] = [];

        while (i < lines.length) {
          const rowLine = lines[i].trim();
          if (rowLine.startsWith("|") && rowLine.endsWith("|")) {
            rows.push(parsePipeLine(rowLine));
            i++;
          } else {
            break;
          }
        }

        const sheetName = currentName || `Sheet${autoIndex++}`;
        const data: CellData[][] = [headerCells, ...rows];
        sheets.push({ name: sheetName, data });
        currentName = null; // reset so next table without heading gets auto-named
      } else {
        i++;
      }
    } else {
      i++;
    }
  }

  // If no tables found, return a single empty sheet
  if (sheets.length === 0) {
    return [{ name: "Sheet1", data: [[{ value: markdown.trim() }]] }];
  }

  return sheets;
}

function parsePipeLine(line: string): CellData[] {
  return line
    .slice(1, -1) // remove leading/trailing |
    .split("|")
    .map((cell) => parseCellFormatting(cell.trim()));
}

function parseCellFormatting(text: string): CellData {
  const bold = /\*\*(.+?)\*\*/.test(text) || /__(.+?)__/.test(text);
  const italic = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/.test(text) || /(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/.test(text);

  const stripped = text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/\*(.+?)\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/_(.+?)_/g, "$1");

  const classNames = [bold && "font-bold", italic && "italic"].filter(Boolean).join(" ");

  return {
    value: stripped,
    ...(bold && { bold }),
    ...(italic && { italic }),
    ...(classNames && { className: classNames }),
  };
}

/**
 * Builds an XLSX Blob from multiple sheets.
 */
export function buildXlsxBlobMultiSheet(sheets: SheetData[]): Blob {
  const wb = XLSX.utils.book_new();

  sheets.forEach((sheet) => {
    const plainData = sheet.data.map((row) => row.map((cell) => cell.value));
    const ws = XLSX.utils.aoa_to_sheet(plainData);

    // Apply bold/italic styles to cells
    sheet.data.forEach((row, rowIdx) => {
      row.forEach((cell, colIdx) => {
        if (cell.bold || cell.italic) {
          const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              font: {
                ...(cell.bold && { bold: true }),
                ...(cell.italic && { italic: true }),
              },
            };
          }
        }
      });
    });

    // Auto-size columns
    const colWidths =
      plainData[0]?.map((_, colIdx) => {
        let maxLen = 10;
        plainData.forEach((row) => {
          const cellVal = row[colIdx] || "";
          maxLen = Math.max(maxLen, cellVal.length);
        });
        return { wch: Math.min(maxLen + 2, 50) };
      }) || [];
    ws["!cols"] = colWidths;

    // Ensure sheet name is valid (max 31 chars, no special chars)
    const safeName = sheet.name.replace(/[\\/*?[\]:]/g, "").slice(0, 31) || "Sheet";
    XLSX.utils.book_append_sheet(wb, ws, safeName);
  });

  const wbOut = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([wbOut], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/**
 * Builds an XLSX Blob from a single sheet's cell data.
 */
export function buildXlsxBlob(
  data: CellData[][],
  sheetName: string = "Sheet1"
): Blob {
  return buildXlsxBlobMultiSheet([{ name: sheetName, data }]);
}
